import { PropsWithChildren, useEffect } from "react";

import {
  Typography,
  CircularProgress,
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@mui/material";
import useFetchDashboardValues from "./hooks/useFetchMetric";
import { useFormikContext } from "formik";
import { RecordWithEnumeratedIndex } from "../client/types";
import { Dates } from "../common/types";
import {
  CardHeading,
  CardWrapper,
  DateSelect,
  ElementWrapper,
  Error,
} from "./common-components";
import { subMonths } from "date-fns";
import { GridExpandMoreIcon } from "@mui/x-data-grid";

const ChurnRateContent = () => {
  const id = "churnRate";
  const { values, isValid } =
    useFormikContext<RecordWithEnumeratedIndex<Dates<typeof id>, number>>();

  const { loading, metric, fetchMetric } = useFetchDashboardValues<{
    churnRate: number;
  }>("churn rate");

  useEffect(() => {
    if (isValid) {
      fetchMetric({
        fromDate: values[`${id}-start-date`],
        toDate: values[`${id}-end-date`],
      });
    }
  }, [values, fetchMetric, id, isValid]);

  const element = () => {
    if (loading) {
      return <CircularProgress />;
    }

    if (typeof metric?.churnRate === "number") {
      return (
        <Box>
          <Typography sx={{ fontSize: 36, marginTop: 2 }}>
            {metric.churnRate}%
          </Typography>
        </Box>
      );
    }

    return <Error />;
  };

  return (
    <>
      <CardHeading heading="Churn Rate" />
      <DateSelect dateName={id} minDate={new Date("01/01/2020")} />
      <ElementWrapper
        styles={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {element()}
      </ElementWrapper>
      <Accordion sx={{ marginTop: 5 }}>
        <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
          <Typography component="span">How is this calculated?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography component="p">
            Churn is calculated by the total number of clients{" "}
            <strong>(T)</strong> minus the number of clients who-reinvested{" "}
            <strong>(I)</strong> within the selected date period, divided by the
            total number of clients as a percentage.
          </Typography>
          <Typography component="p" sx={{ marginTop: 3 }}>
            <strong>T</strong> is defined as any client with at least one
            investment within the selected date range
          </Typography>
          <Typography component="p" sx={{ marginTop: 1 }}>
            <strong>I</strong> is defined as any client who has made at least
            two investments on separate days. (Since we don't currently have
            access to invoice numbers, investments on different days are treated
            as separate invoices.)
          </Typography>
          <Typography component="p" sx={{ marginTop: 1 }}>
            So then the churn rate = <strong>[(T-I)/T]*100</strong>
          </Typography>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

const ChurnRateWrapper = ({ children }: PropsWithChildren) => (
  <CardWrapper id="churnRate" defaultFromDate={subMonths(new Date(), 18)}>
    {children}
  </CardWrapper>
);

export const ChurnRateCard = () => (
  <ChurnRateWrapper>
    <ChurnRateContent />
  </ChurnRateWrapper>
);
